import { useCallback, useEffect, useRef, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import {
	describeRequestError,
	listMaintenanceRecords,
	listTenantOptions,
	listTenantServices,
} from "./requests/maintenanceWindowsApi";
import type {
	MaintenanceRecordRow,
	MaintenanceRecordStatusFilter,
	TenantOption,
	TenantServiceOption,
} from "./types";

interface Loadable<T> {
	data: T;
	loading: boolean;
	error: string | null;
}

/**
 * Runs `load` whenever its identity changes and keeps only the newest answer:
 * switching tabs or customers quickly would otherwise let a slow earlier
 * request overwrite a fresher one.
 */
function useLatest<T>(
	load: () => Promise<T>,
	initial: T
): Loadable<T> & { refresh: () => Promise<void> } {
	const [state, setState] = useState<Loadable<T>>({ data: initial, loading: true, error: null });
	const requestIdRef = useRef(0);

	const run = useCallback(async () => {
		const requestId = ++requestIdRef.current;
		setState((prev) => ({ ...prev, loading: true }));
		try {
			const data = await load();
			if (requestId === requestIdRef.current) setState({ data, loading: false, error: null });
		} catch (err) {
			if (requestId === requestIdRef.current) {
				setState((prev) => ({ ...prev, loading: false, error: describeRequestError(err) }));
			}
		}
	}, [load]);

	useEffect(() => {
		void run();
	}, [run]);

	return { ...state, refresh: run };
}

/** The rows of one status tab. */
export function useMaintenanceRecords(status: MaintenanceRecordStatusFilter) {
	const api = useAxiosAuth0();
	const load = useCallback(() => listMaintenanceRecords(api, status), [api, status]);
	return useLatest<MaintenanceRecordRow[]>(load, []);
}

/** Every tenant the customer picker offers. */
export function useTenantOptions() {
	const api = useAxiosAuth0();
	const load = useCallback(() => listTenantOptions(api), [api]);
	return useLatest<TenantOption[]>(load, []);
}

/**
 * Services of the selected customers, refetched when the selection changes.
 * Keyed on the sorted ids so reordering the selection does not refetch.
 */
export function useTenantServices(tenantIds: number[]) {
	const api = useAxiosAuth0();
	const key = [...tenantIds].sort((a, b) => a - b).join(",");
	const load = useCallback(
		() => listTenantServices(api, key ? key.split(",").map(Number) : []),
		[api, key]
	);
	return useLatest<TenantServiceOption[]>(load, []);
}
